import { useState, useCallback, useRef } from 'react';
import type { BriefSection, CascadeWarning } from '../types/canvas';

interface RefineTurn {
  role: 'user' | 'assistant';
  text: string;
}

interface TurnsInfo {
  used: number;
  max: number;
}

interface RefineState {
  activeSectionId: string | null;
  turns: RefineTurn[];
  streaming: boolean;
  streamingText: string;
  cascadeWarnings: CascadeWarning[];
  open: (sectionId: string) => void;
  close: () => void;
  sendRefine: (sessionId: string, sectionId: string, message: string) => void;
  sendRandomize: (sessionId: string, sectionId: string) => void;
  sendApprove: (sessionId: string, sectionId: string) => void;
  dismissWarning: (affectedSection: string) => void;
  onSectionUpdate: (cb: (section: BriefSection) => void) => void;
  onApproval: (cb: (sectionId: string, approvalCount: number) => void) => void;
  getTurnsInfo: (sectionId: string) => TurnsInfo | null;
}

export function useRefine(): RefineState {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<RefineTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [cascadeWarnings, setCascadeWarnings] = useState<CascadeWarning[]>([]);
  const [sectionTurnsMap, setSectionTurnsMap] = useState<Record<string, TurnsInfo>>({});

  const sectionUpdateCb = useRef<((section: BriefSection) => void) | null>(null);
  const approvalCb = useRef<((sectionId: string, approvalCount: number) => void) | null>(null);

  const onSectionUpdate = useCallback((cb: (section: BriefSection) => void) => {
    sectionUpdateCb.current = cb;
  }, []);

  const onApproval = useCallback((cb: (sectionId: string, approvalCount: number) => void) => {
    approvalCb.current = cb;
  }, []);

  const open = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
    setTurns([]);
    setStreamingText('');
  }, []);

  const close = useCallback(() => {
    setActiveSectionId(null);
    setTurns([]);
    setStreamingText('');
  }, []);

  const dismissWarning = useCallback((affectedSection: string) => {
    setCascadeWarnings((prev) => prev.filter((w) => w.affectedSection !== affectedSection));
  }, []);

  const streamAction = useCallback(async (
    sessionId: string,
    sectionId: string,
    action: 'refine' | 'randomize',
    message?: string,
  ) => {
    setStreaming(true);
    setStreamingText('');

    if (action === 'refine' && message) {
      setTurns((prev) => [...prev, { role: 'user', text: message }]);
    }

    try {
      const refineStreamUrl = import.meta.env.VITE_REFINE_STREAM_URL ?? '/api/brief/refine-stream';
      const res = await fetch(refineStreamUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, sectionId, action, message }),
      });

      if (!res.ok || !res.body) {
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6);
          try {
            const event = JSON.parse(json);
            if (event.type === 'delta') {
              fullText += event.text;
              setStreamingText(fullText);
            } else if (event.type === 'section') {
              setTurns((prev) => [...prev, { role: 'assistant', text: 'Updated.' }]);
              if (event.sectionTurnsUsed != null && event.sectionTurnsMax != null) {
                setSectionTurnsMap((prev) => ({ ...prev, [sectionId]: { used: event.sectionTurnsUsed, max: event.sectionTurnsMax } }));
              }
              sectionUpdateCb.current?.(event.section);
            } else if (event.type === 'error' && (event.code === 'SECTION_LIMIT' || event.code === 'BRIEF_LIMIT')) {
              if (event.sectionTurnsUsed != null && event.sectionTurnsMax != null) {
                setSectionTurnsMap((prev) => ({ ...prev, [sectionId]: { used: event.sectionTurnsUsed, max: event.sectionTurnsMax } }));
              }
              setTurns((prev) => [...prev, { role: 'assistant', text: event.message }]);
            } else if (event.type === 'cascade') {
              const warnings = event.warnings as CascadeWarning[];
              setCascadeWarnings((prev) => {
                const filtered = prev.filter((w) => !warnings.some((nw) => nw.affectedSection === w.affectedSection));
                return [...filtered, ...warnings];
              });
            } else if (event.type === 'error') {
              setTurns((prev) => [...prev, { role: 'assistant', text: `Error: ${event.message}` }]);
            }
          } catch { /* skip malformed line */ }
        }
      }
    } catch {
      setTurns((prev) => [...prev, { role: 'assistant', text: 'Connection error.' }]);
    }

    setStreaming(false);
    setStreamingText('');
  }, []);

  const sendRefine = useCallback((sessionId: string, sectionId: string, message: string) => {
    streamAction(sessionId, sectionId, 'refine', message);
  }, [streamAction]);

  const sendRandomize = useCallback((sessionId: string, sectionId: string) => {
    streamAction(sessionId, sectionId, 'randomize');
  }, [streamAction]);

  const sendApprove = useCallback(async (sessionId: string, sectionId: string) => {
    try {
      const refineStreamUrl = import.meta.env.VITE_REFINE_STREAM_URL ?? '/api/brief/refine-stream';
      const res = await fetch(refineStreamUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, sectionId, action: 'approve' }),
      });
      if (!res.ok || !res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'approved') {
              approvalCb.current?.(event.sectionId, event.approvalCount);
            }
          } catch { /* skip */ }
        }
      }
    } catch { /* silent */ }
  }, []);

  const getTurnsInfo = useCallback((sectionId: string): TurnsInfo | null => {
    return sectionTurnsMap[sectionId] ?? null;
  }, [sectionTurnsMap]);

  return {
    activeSectionId,
    turns,
    streaming,
    streamingText,
    cascadeWarnings,
    open,
    close,
    sendRefine,
    sendRandomize,
    sendApprove,
    dismissWarning,
    onSectionUpdate,
    onApproval,
    getTurnsInfo,
  };
}
